<template>
  <section class="member-dashboard py-5 px-4">
    <b-container fluid>
      <!-- Header acogedor -->
      <div class="member-header mb-4">
        <h1 class="member-title">Mi plan</h1>
        <p class="member-subtitle">
          Tu plan activo, fechas de vigencia y beneficios incluidos
        </p>
      </div>

      <b-spinner v-if="loading" label="Cargando..." class="d-block mx-auto my-5"></b-spinner>

      <b-alert
        v-if="errorMessage"
        variant="danger"
        :show="Boolean(errorMessage)"
        dismissible
        class="mt-3"
        @dismissed="errorMessage = null"
      >
        {{ errorMessage }}
      </b-alert>

      <div v-if="!loading && !errorMessage && plans.length === 0 && !managedCompanies" class="empty-state text-center py-5">
        <div class="empty-state-icon mb-3">
          <i class="fas fa-clipboard-list fa-3x text-muted"></i>
        </div>
        <p class="text-muted mb-0">No hay planes activos.</p>
        <p class="text-muted small mt-1">Si adquiriste un plan, verifica tu sesión o contacta a soporte.</p>
      </div>

      <!-- Bloques de plan (estilo DetallesDelPlan: imagen + beneficios) -->
      <div
        v-for="(plan, index) in plans"
        :key="plan.id || index"
        class="plan-detail-block member-plan-block"
        :class="{ 'plan-block--image-right': index % 2 === 1 }"
      >
        <div
          class="plan-block-card"
          :class="{ 'plan-block-card--cancelled': isPlanCancelled(plan) }"
        >
          <div class="plan-block-header-inner">
            <h2 class="plan-block-title">{{ planName(plan) }}</h2>
            <b-badge :variant="payStatusVariant(plan)" class="plan-status-badge">
              <i class="fas mr-1" :class="payStatusIcon(plan.pay_status)"></i>
              {{ payStatusLabel(plan) }}
            </b-badge>
          </div>

          <b-alert
            v-if="isPaymentFailed(plan)"
            show
            variant="danger"
            class="mt-3 mb-0"
          >
            No pudimos cobrar tu renovación.
            <div v-if="plan.pending_invoice_url && isBillingPayer(plan)" class="mt-3 mb-0">
              <b-button variant="danger" @click="abrirFacturaPendiente(plan)">
                Pagar factura pendiente
              </b-button>
            </div>
          </b-alert>

          <b-alert
            v-else-if="hasScheduledCancel(plan)"
            show
            variant="warning"
            class="mt-3 mb-0"
          >
            Se cancela el {{ formatDate(plan.cancel_at) }}. Conservas tus beneficios hasta esa fecha.
          </b-alert>

          <!-- Bloque de marketing cuando el plan está cancelado -->
          <div v-if="isPlanCancelled(plan)" class="plan-cancelled-message">
            <div class="plan-cancelled-message-inner">
              <div class="plan-cancelled-icon">
                <i class="fas fa-heart-broken"></i>
              </div>
              <h3 class="plan-cancelled-title">Lo que te estás perdiendo</h3>
              <p class="plan-cancelled-subtitle">
                Al cancelar este plan dejaste de tener acceso a beneficios que cuidan tu salud y la de los tuyos.
              </p>
              <ul class="plan-cancelled-benefits">
                <li v-for="(benefit, bidx) in allBenefits(plan)" :key="'lost-' + bidx" class="plan-cancelled-benefit-item">
                  <i class="fas fa-check-circle"></i>
                  <span><strong>{{ benefit.name }}</strong><span v-if="benefit.detail"> — {{ benefit.detail }}</span></span>
                </li>
              </ul>
              <p class="plan-cancelled-cta-text">Vuelve a activar tu plan y recupera todo por solo <strong>${{ planPrice(plan) }}{{ priceSuffix(plan) }}</strong>.</p>
              <b-button
                v-if="isBillingPayer(plan)"
                variant="warning"
                size="lg"
                class="btn-reactivate-plan w-100"
                @click="comprarDeNuevo(plan)"
              >
                <i class="fas fa-redo mr-2"></i>
                Comprar de nuevo
              </b-button>
            </div>
          </div>

          <div class="plan-block-content">
            <div class="plan-block-image-col" :class="{ 'plan-block-image-col--muted': isPlanCancelled(plan) }">
              <div class="plan-image-wrapper">
                <img :src="getPlanImage(plan)" :alt="planName(plan)" class="plan-block-image" />
                <div class="plan-price-overlay">
                  <span class="plan-price-value">${{ planPrice(plan) }}</span>
                  <span class="plan-price-period">{{ priceSuffix(plan) }}</span>
                </div>
              </div>
              <div v-if="planValidity(plan)" class="plan-validity-badge">
                <i class="fas fa-calendar-alt mr-2"></i>
                {{ planValidity(plan) }} meses de vigencia
              </div>
              <div class="plan-dates-row">
                <span class="plan-date-item">
                  <i class="fas fa-play-circle mr-1"></i>
                  Inicio {{ formatDate(plan.start_date) }}
                </span>
                <span class="plan-date-item">
                  <i class="fas fa-flag-checkered mr-1"></i>
                  Fin {{ formatDate(plan.end_date) }}
                </span>
              </div>
              <b-button
                v-if="canPayPlan(plan)"
                variant="primary"
                size="lg"
                class="btn-pay-plan w-100 mt-3"
                :disabled="checkoutLoading === plan.id"
                @click="pagarMembresia(plan)"
              >
                <b-spinner v-if="checkoutLoading === plan.id" small class="mr-2"></b-spinner>
                <i v-else class="fas fa-credit-card mr-2"></i>
                {{ checkoutLoading === plan.id ? 'Redirigiendo...' : 'Pagar plan — $' + formatAmount(planPrice(plan)) }}
              </b-button>
              <b-button
                v-if="canCancelPlan(plan)"
                variant="outline-danger"
                size="lg"
                class="btn-cancel-plan w-100 mt-2"
                :disabled="cancelLoading === plan.id"
                @click="cancelarMembresia(plan)"
              >
                <b-spinner v-if="cancelLoading === plan.id" small class="mr-2"></b-spinner>
                <i v-else class="fas fa-ban mr-2"></i>
                {{ cancelLoading === plan.id ? 'Cancelando...' : 'Cancelar membresía' }}
              </b-button>
              <b-button
                v-if="canResumePlan(plan)"
                variant="outline-primary"
                size="lg"
                class="btn-resume-plan w-100 mt-2"
                :disabled="resumeLoading === plan.id"
                @click="reanudarMembresia(plan)"
              >
                <b-spinner v-if="resumeLoading === plan.id" small class="mr-2"></b-spinner>
                <i v-else class="fas fa-play mr-2"></i>
                {{ resumeLoading === plan.id ? 'Reanudando...' : 'Reanudar' }}
              </b-button>
            </div>
            <div class="plan-block-benefits-col">
              <h4 class="benefits-title">
                <i class="fas fa-gift mr-2"></i>
                {{ isPlanCancelled(plan) ? 'Beneficios que tenías' : 'Beneficios incluidos' }}
              </h4>
              <ul class="benefits-list" v-if="allBenefits(plan).length > 0">
                <li v-for="(benefit, bidx) in allBenefits(plan)" :key="bidx" class="benefit-row" :class="{ 'benefit-row--cancelled': isPlanCancelled(plan) }">
                  <img :src="benefitCheckIcon" alt="" class="benefit-check-img" />
                  <div class="benefit-item">
                    <span class="benefit-name">{{ benefit.name }}</span>
                    <span v-if="benefit.detail" class="benefit-detail">{{ benefit.detail }}</span>
                  </div>
                </li>
              </ul>
              <div v-else class="no-benefits">
                <p class="text-muted mb-0">Sin beneficios definidos para este plan.</p>
              </div>
              <div v-if="!isPlanCancelled(plan)" class="plan-seller-info mt-4">
                <div class="seller-label">
                  <i class="fas fa-user-tie mr-2"></i>
                  Tu agente
                </div>
                <div class="seller-name">{{ sellerLabel(plan) || 'Sin agente asignado' }}</div>
              </div>
            </div>
          </div>

          <member-family-panel v-if="showFamilyPanel(plan)" :membership-id="plan.id" :plan="plan" />
          <member-guarantee-card v-if="showGuarantee(plan)" :subscription-id="subscriptionId(plan)" />

          <!-- Sección inferior: Consultas realizadas y Uso de beneficios -->
          <div v-if="!isPlanCancelled(plan)" class="plan-detail-footer">
            <!-- Consultas realizadas -->
            <div v-if="planAppointments(plan).length > 0" class="detail-section">
              <h5 class="detail-section-title">
                <i class="fas fa-calendar-check mr-2"></i>
                Consultas realizadas
              </h5>
              <div class="consultations-list">
                <div
                  v-for="apt in sortedAppointments(plan)"
                  :key="apt.id"
                  class="consultation-card"
                >
                  <div class="consultation-main">
                    <span class="consultation-date">
                      <i class="fas fa-calendar-day mr-1"></i>
                      {{ formatVisitDate(apt.visit_date) }}
                    </span>
                    <span class="consultation-reason">{{ apt.reason_visit || 'Consulta' }}</span>
                    <span v-if="apt.facilities && apt.facilities.name" class="consultation-facility text-muted small">
                      <i class="fas fa-map-marker-alt mr-1"></i>
                      {{ apt.facilities.name }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Beneficios detallados con estado activo/inactivo -->
            <div v-if="detailBenefits(plan).length > 0" class="detail-section mt-4">
              <h5 class="detail-section-title">
                <i class="fas fa-chart-pie mr-2"></i>
                Uso de beneficios
              </h5>
              <div class="benefits-detail-grid">
                <div
                  v-for="(item, idx) in detailBenefits(plan)"
                  :key="'ben-' + idx"
                  class="benefit-detail-card"
                  :class="{ 'benefit-detail-card--inactive': !item.isActive }"
                >
                  <div class="benefit-detail-icon" :class="{ 'benefit-detail-icon--inactive': !item.isActive }">
                    <i :class="item.icon"></i>
                  </div>
                  <div class="benefit-detail-body">
                    <span class="benefit-detail-name">{{ item.name }}</span>
                    <span class="benefit-detail-count">
                      {{ item.countUsed }} / {{ item.countTotal }} usados
                    </span>
                    <span v-if="!item.isActive" class="benefit-detail-available-date">
                      <i class="fas fa-clock mr-1"></i>
                      Disponible desde {{ formatDate(item.activationDate) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <member-company-panel @loaded="managedCompanies = $event" />
    </b-container>
  </section>
</template>

<script>
import { BContainer, BSpinner, BAlert, BBadge, BButton } from 'bootstrap-vue'
import memberService from '@/core/services/api/member'
import { billingSuffix, chargeAmount, isBillingPayer, planKindOf, resolvedBilling } from '@/core/utils/membershipBilling'
import MemberCompanyPanel from './MemberCompanyPanel.vue'
import MemberFamilyPanel from './MemberFamilyPanel.vue'
import MemberGuaranteeCard from './MemberGuaranteeCard.vue'

const ROLE_MEMBER_ID = 10

/** Mapeo de plan name (partial match) a imagen para mostrar el plan del miembro */
const PLAN_DETAIL_IMAGE_MAP = [
  { keywords: ['plus', 'tu salud plus'], image: require('@/assets/lp/plans/5.png') },
  { keywords: ['familiar', 'familia'], image: require('@/assets/lp/plans/7.png') },
  { keywords: ['tu salud', 'individual', 'medico'], image: require('@/assets/lp/plans/6.png') },
]

export default {
  name: 'MemberDashboard',
  components: {
    BContainer,
    BSpinner,
    BAlert,
    BBadge,
    BButton,
    MemberCompanyPanel,
    MemberFamilyPanel,
    MemberGuaranteeCard,
  },
  data() {
    return {
      loading: false,
      errorMessage: null,
      plans: [],
      managedCompanies: 0,
      checkoutLoading: null,
      cancelLoading: null,
      resumeLoading: null,
      benefitCheckIcon: require('@/assets/lp/plans/icons/Recurso 25@300x.png'),
    }
  },
  mounted() {
    this.fetchMemberPlans()
  },
  methods: {
    canPayPlan(plan) {
      return this.isBillingPayer(plan) && (plan.pay_status === 0 || plan.pay_status === 2)
    },
    hasScheduledCancel(plan) {
      return plan.pay_status === 1 && !!plan.cancel_at
    },
    canCancelPlan(plan) {
      return this.isBillingPayer(plan) && plan.pay_status === 1 && !plan.cancel_at
    },
    canResumePlan(plan) {
      return this.isBillingPayer(plan) && this.hasScheduledCancel(plan)
    },
    isBillingPayer(plan) {
      return isBillingPayer(plan)
    },
    showFamilyPanel(plan) {
      if (!this.isBillingPayer(plan) || !plan || !plan.id) return false
      if (planKindOf(plan) === 'family') return true
      const billing = resolvedBilling(plan)
      const quantity = billing && Number(billing.quantity)
      return quantity > 1
    },
    subscriptionId(plan) {
      return plan && (plan.membership_subscription_id || (plan.subscription && plan.subscription.id))
    },
    showGuarantee(plan) {
      return this.isBillingPayer(plan) && !!this.subscriptionId(plan)
    },
    isPaymentFailed(plan) {
      return plan.pay_status === 3
    },
    /** Plan con suscripción cancelada (pay_status 4) */
    isPlanCancelled(plan) {
      return plan.pay_status === 4
    },
    payStatusIcon(payStatus) {
      if (payStatus === 1) return 'fa-check-circle'
      if (payStatus === 0) return 'fa-exclamation-circle'
      if (payStatus === 2) return 'fa-clock'
      if (payStatus === 3) return 'fa-times-circle'
      if (payStatus === 4) return 'fa-ban'
      return 'fa-question-circle'
    },
    apiErrorMessage(err, fallback) {
      const data = err && err.response && err.response.data
      const raw = data && (data.message != null ? data.message : data.error)
      if (Array.isArray(raw)) return raw.filter(Boolean).join(' ') || fallback
      if (typeof raw === 'string' && raw.trim()) return raw.trim()
      return fallback
    },
    abrirFacturaPendiente(plan) {
      if (plan && plan.pending_invoice_url) {
        window.location.href = plan.pending_invoice_url
      }
    },
    pendingInvoiceUrl(err) {
      const status = err && err.response && err.response.status
      const data = err && err.response && err.response.data
      if (status !== 409 || !data) return null
      const type = data.type || (data.data && data.data.type)
      if (type !== 'pending_invoice') return null
      return data.pending_invoice_url
        || data.url
        || data.invoice_url
        || (data.data && (data.data.pending_invoice_url || data.data.url || data.data.invoice_url))
        || null
    },
    comprarDeNuevo(plan) {
      const params = plan && plan.memberships_parameters
      const planId = params && params.id
      if (!planId) {
        this.$router.push({ path: '/plans', hash: 'planes-2027' }).catch(() => {})
        return
      }
      this.$router.push({
        name: 'comprar-plan',
        params: { planId: String(planId) }
      }).catch(() => {})
    },
    async pagarMembresia(plan) {
      const membershipId = plan.id
      if (!membershipId) return
      this.checkoutLoading = membershipId
      this.errorMessage = null
      try {
        const data = await memberService.createCheckout(membershipId)
        const checkoutUrl = data && (data.checkoutUrl || data.checkout_url || (data.data && data.data.checkout_url))
        if (checkoutUrl) {
          window.location.href = checkoutUrl
          return
        }
        this.errorMessage = 'No se pudo obtener la página de pago. Intenta de nuevo.'
      } catch (err) {
        const pendingUrl = this.pendingInvoiceUrl(err)
        if (pendingUrl) {
          window.location.href = pendingUrl
          return
        }
        console.error('Error al iniciar checkout:', err)
        this.errorMessage = this.apiErrorMessage(err, 'No se pudo iniciar el pago. Intenta de nuevo.')
      } finally {
        this.checkoutLoading = null
      }
    },
    async cancelarMembresia(plan) {
      const membershipId = plan.id
      if (!membershipId) return
      this.cancelLoading = membershipId
      this.errorMessage = null
      try {
        await memberService.cancelMembership(membershipId)
        await this.fetchMemberPlans()
      } catch (err) {
        console.error('Error al cancelar la membresía:', err)
        this.errorMessage = this.apiErrorMessage(err, 'No se pudo cancelar la membresía. Intenta de nuevo.')
      } finally {
        this.cancelLoading = null
      }
    },
    async reanudarMembresia(plan) {
      const membershipId = plan.id
      if (!membershipId) return
      this.resumeLoading = membershipId
      this.errorMessage = null
      try {
        await memberService.resumeMembership(membershipId)
        await this.fetchMemberPlans()
      } catch (err) {
        console.error('Error al reanudar la membresía:', err)
        this.errorMessage = this.apiErrorMessage(err, 'No se pudo reanudar la membresía. Intenta de nuevo.')
      } finally {
        this.resumeLoading = null
      }
    },
    async fetchMemberPlans() {
      this.loading = true
      this.errorMessage = null

      try {
        const response = await memberService.filterByRole(ROLE_MEMBER_ID)

        if (process.env.NODE_ENV !== 'production') {
          console.log('Member plan filter response:', response)
        }

        if (response?.status === 'OK' && response?.data && typeof response.data === 'object') {
          this.plans = this.normalizePlans(response.data)
        } else {
          this.plans = []
        }
      } catch (error) {
        console.error('Error fetching member plans:', error)
        this.errorMessage = 'No se pudo cargar la información del plan. Intenta de nuevo más tarde.'
      } finally {
        this.loading = false
      }
    },
    normalizePlans(data) {
      if (Array.isArray(data)) return data
      const arrays = Object.values(data).filter(Array.isArray)
      return arrays.length ? arrays.flat() : []
    },
    planName(plan) {
      const params = plan.memberships_parameters
      return (params && params.name) || 'Plan activo'
    },
    planPrice(plan) {
      return chargeAmount(plan)
    },
    priceSuffix(plan) {
      return billingSuffix(plan)
    },
    planValidity(plan) {
      const params = plan.memberships_parameters
      return params && params.validity != null ? params.validity : null
    },
    payStatusVariant(plan) {
      const payStatus = plan && typeof plan === 'object' ? plan.pay_status : plan
      if (payStatus === 1 && plan && plan.cancel_at) return 'warning'
      if (payStatus === 1) return 'success'
      if (payStatus === 0) return 'danger'
      if (payStatus === 2) return 'warning'
      if (payStatus === 3) return 'danger'
      if (payStatus === 4) return 'warning'
      return 'secondary'
    },
    payStatusLabel(plan) {
      const payStatus = plan && typeof plan === 'object' ? plan.pay_status : plan
      if (payStatus === 0) return 'Sin pagar'
      if (payStatus === 1 && plan && plan.cancel_at) {
        return `Se cancela el ${this.formatDate(plan.cancel_at)}`
      }
      if (payStatus === 1) return 'Pagado'
      if (payStatus === 2) return 'Pendiente'
      if (payStatus === 3) return 'Pago fallido'
      if (payStatus === 4) return 'Plan cancelado'
      return 'Estado desconocido'
    },
    /**
     * Formatea fechas ISO o YYYY-MM-DD como fecha local (evita corrimiento UTC).
     * @param {string} dateStr
     */
    formatDate(dateStr) {
      if (!dateStr) return '—'
      const raw = String(dateStr).trim()
      const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (ymd) {
        const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        return d.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
      }
      const d = new Date(raw)
      return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    /** Formatea visit_date (MM-DD-YYYY) para mostrar en consultas */
    formatVisitDate(str) {
      if (!str) return '—'
      const parts = String(str).split('-')
      if (parts.length === 3) {
        const [m, d, y] = parts.map(Number)
        if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
          const dObj = new Date(y, m - 1, d)
          return dObj.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
        }
      }
      return str
    },
    formatAmount(val) {
      return val != null ? val : 0
    },
    getPlanImage(plan) {
      const name = ((plan.memberships_parameters && plan.memberships_parameters.name) || '').toLowerCase()
      for (const { keywords, image } of PLAN_DETAIL_IMAGE_MAP) {
        if (keywords.some(k => name.includes(k))) return image
      }
      return require('@/assets/lp/plans/6.png')
    },
    /**
     * Construye la lista unificada de beneficios desde memberships_parameters
     */
    allBenefits(plan) {
      const params = plan.memberships_parameters
      if (!params) return []

      const items = []

      if (params.consultations && params.consultations.length > 0) {
        params.consultations.forEach(c => {
          const count = (c.pivot && c.pivot.count != null) ? c.pivot.count : c.count
          items.push({
            name: 'Consultas',
            detail: count != null ? `${count} incluidas` : '',
          })
        })
      }
      if (params.procedures && params.procedures.length > 0) {
        params.procedures.forEach(p => {
          const count = p.pivot && p.pivot.count != null ? p.pivot.count : null
          items.push({
            name: p.name || 'Procedimiento',
            detail: count != null ? `${count} incl.` : (p.price ? `$${p.price}` : ''),
          })
        })
      }
      if (params.laboratories && params.laboratories.length > 0) {
        params.laboratories.forEach(l => {
          const count = l.pivot && l.pivot.count != null ? l.pivot.count : null
          items.push({
            name: l.name || 'Laboratorio',
            detail: count != null ? `${count} incl.` : (l.price ? `$${l.price}` : ''),
          })
        })
      }
      if (params.tests && params.tests.length > 0) {
        params.tests.forEach(t => {
          const count = t.pivot && t.pivot.count != null ? t.pivot.count : null
          items.push({
            name: t.name || 'Prueba',
            detail: count != null ? `${count} incl.` : (t.price ? `$${t.price}` : ''),
          })
        })
      }
      if (params.diagnostic_imagings && params.diagnostic_imagings.length > 0) {
        params.diagnostic_imagings.forEach(d => {
          items.push({ name: d.name || 'Imagen diagnóstica', detail: '' })
        })
      }

      return items
    },
    sellerLabel(plan) {
      const s = plan.seller
      if (!s) return ''
      const name = (s.name || '').trim()
      const lastname = (s.lastname || '').trim()
      if (name || lastname) return (name + ' ' + lastname).trim()
      return ''
    },
    /** Obtiene las citas del plan (appointments) */
    planAppointments(plan) {
      return (plan.appointments && Array.isArray(plan.appointments)) ? plan.appointments : []
    },
    /** Ordena citas por fecha descendente (más reciente primero) */
    sortedAppointments(plan) {
      const list = this.planAppointments(plan)
      return [...list].sort((a, b) => {
        const da = this.parseVisitDate(a.visit_date)
        const db = this.parseVisitDate(b.visit_date)
        return db - da
      })
    },
    /** Parsea visit_date en formato MM-DD-YYYY */
    parseVisitDate(str) {
      if (!str) return 0
      const parts = String(str).split('-')
      if (parts.length === 3) {
        const [m, d, y] = parts.map(Number)
        if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
          const dObj = new Date(y, m - 1, d)
          return dObj.getTime()
        }
      }
      return new Date(str).getTime() || 0
    },
    /**
     * Indica si una fecha de activación ya pasó (beneficio activo)
     * @param {string} activationDate - Fecha en YYYY-MM-DD
     */
    isBenefitActive(activationDate) {
      if (!activationDate) return true
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const parts = String(activationDate).split('-')
      if (parts.length === 3) {
        const [y, m, d] = parts.map(Number)
        const actDate = new Date(y, m - 1, d)
        return actDate <= today
      }
      return true
    },
    /**
     * Lista detallada de beneficios con estado activo/inactivo y uso
     */
    detailBenefits(plan) {
      const params = plan.memberships_parameters
      if (!params) return []

      const items = []

      if (params.consultations && params.consultations.length > 0) {
        params.consultations.forEach(c => {
          const total = c.count != null ? c.count : 0
          const used = c.count_used != null ? c.count_used : 0
          const actDate = c.activation_date || null
          items.push({
            name: 'Consultas',
            countUsed: used,
            countTotal: total,
            activationDate: actDate,
            isActive: this.isBenefitActive(actDate),
            icon: 'fas fa-stethoscope',
          })
        })
      }
      if (params.procedures && params.procedures.length > 0) {
        params.procedures.forEach(p => {
          const pivot = p.pivot || {}
          const total = pivot.count != null ? pivot.count : 1
          const used = p.count_used != null ? p.count_used : 0
          const actDate = p.activation_date || null
          items.push({
            name: p.name || 'Procedimiento',
            countUsed: used,
            countTotal: total,
            activationDate: actDate,
            isActive: this.isBenefitActive(actDate),
            icon: 'fas fa-procedures',
          })
        })
      }
      if (params.laboratories && params.laboratories.length > 0) {
        params.laboratories.forEach(l => {
          const pivot = l.pivot || {}
          const total = pivot.count != null ? pivot.count : 1
          const used = l.count_used != null ? l.count_used : 0
          const actDate = l.activation_date || null
          items.push({
            name: l.name || 'Laboratorio',
            countUsed: used,
            countTotal: total,
            activationDate: actDate,
            isActive: this.isBenefitActive(actDate),
            icon: 'fas fa-vial',
          })
        })
      }
      if (params.tests && params.tests.length > 0) {
        params.tests.forEach(t => {
          const pivot = t.pivot || {}
          const total = pivot.count != null ? pivot.count : 1
          const used = t.count_used != null ? t.count_used : 0
          const actDate = t.activation_date || null
          items.push({
            name: t.name || 'Prueba',
            countUsed: used,
            countTotal: total,
            activationDate: actDate,
            isActive: this.isBenefitActive(actDate),
            icon: 'fas fa-flask',
          })
        })
      }

      return items
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../landings/styles/variables';

.member-dashboard {
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.member-header {
  .member-title {
    font-size: clamp(1.75rem, 4vw, 2.25rem);
    font-weight: 700;
    color: $landing-primary-color;
    margin-bottom: 0.35rem;
    letter-spacing: 0.02em;
  }

  .member-subtitle {
    font-size: 1.05rem;
    color: $landing-text-medium;
    margin: 0;
    line-height: 1.5;
  }
}

.empty-state {
  .empty-state-icon {
    opacity: 0.6;
  }
}

/* Bloque de plan (inspirado en DetallesDelPlan) */
.member-plan-block {
  margin-bottom: 2.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.plan-block-card {
  background: $landing-bg-white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px $landing-primary-rgba-08;
  border: 1px solid $landing-border-light;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 28px $landing-primary-rgba-12;
  }

  &.plan-block-card--cancelled {
    border-color: rgba(253, 126, 20, 0.35);
    box-shadow: 0 4px 20px rgba(253, 126, 20, 0.12);

    &:hover {
      box-shadow: 0 8px 28px rgba(253, 126, 20, 0.18);
    }
  }
}

/* Bloque de marketing cuando el plan está cancelado */
.plan-cancelled-message {
  padding: 1.5rem 1.5rem 1.25rem;
  background: linear-gradient(135deg, #fff8f0 0%, #fff4e6 50%, #fff0dc 100%);
  border-bottom: 2px solid rgba(253, 126, 20, 0.25);
}

.plan-cancelled-message-inner {
  max-width: 100%;
}

.plan-cancelled-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #fd7e14 0%, #e8590c 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.35rem;
  box-shadow: 0 4px 12px rgba(253, 126, 20, 0.35);
}

.plan-cancelled-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #c2410c;
  margin: 0 0 0.5rem;
  letter-spacing: 0.02em;
}

.plan-cancelled-subtitle {
  font-size: 0.95rem;
  color: #9a3412;
  margin: 0 0 1rem;
  line-height: 1.45;
  opacity: 0.95;
}

.plan-cancelled-benefits {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
}

.plan-cancelled-benefit-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #9a3412;

  i {
    color: #e8590c;
    font-size: 1rem;
    flex-shrink: 0;
  }
}

.plan-cancelled-cta-text {
  font-size: 0.95rem;
  color: #9a3412;
  margin: 0 0 1rem;
  font-weight: 500;
}

.btn-reactivate-plan {
  font-weight: 700;
  padding: 0.85rem 1.25rem;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #fd7e14 0%, #e8590c 100%) !important;
  color: white !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(253, 126, 20, 0.4);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(253, 126, 20, 0.5);
  }
}

.plan-block-card--cancelled .plan-block-header-inner {
  background: linear-gradient(135deg, #c2410c 0%, #9a3412 50%, #7c2d12 100%);
  color: white;
}

.plan-block-header-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, $landing-primary-color 0%, $landing-secondary-color 100%);
  color: white;

  .plan-block-title {
    font-size: clamp(1.35rem, 3vw, 1.65rem);
    font-weight: 700;
    margin: 0;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .plan-status-badge {
    font-size: 0.85rem;
    padding: 0.4rem 0.85rem;
    border-radius: 8px;
    font-weight: 600;
  }
}

.plan-block-content {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 2rem 1.5rem;
  align-items: stretch;

  &.plan-block--image-right {
    flex-direction: row-reverse;
  }
}

.plan-block-image-col {
  flex: 0 0 42%;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .plan-image-wrapper {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: 3/4;
    background: $landing-bg-light;

    .plan-block-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .plan-price-overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(transparent, rgba(13, 43, 69, 0.9));
      color: white;
      padding: 1rem 1.25rem;
      text-align: center;

      .plan-price-value {
        font-size: 2.25rem;
        font-weight: 700;
        line-height: 1.1;
      }

      .plan-price-period {
        font-size: 1.15rem;
        opacity: 0.95;
        margin-left: 0.2rem;
      }
    }
  }

  &.plan-block-image-col--muted .plan-image-wrapper {
    opacity: 0.82;

    .plan-price-overlay {
      background: linear-gradient(transparent, rgba(100, 100, 100, 0.75));
    }
  }

  .plan-validity-badge {
    margin-top: 1rem;
    padding: 0.6rem 1rem;
    background: $landing-bg-light;
    border-radius: 8px;
    color: $landing-primary-color;
    font-size: 0.9rem;
    font-weight: 600;

    i {
      color: $landing-secondary-color;
    }
  }

  .plan-dates-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.75rem;
    font-size: 0.9rem;
    color: $landing-text-medium;

    .plan-date-item {
      display: flex;
      align-items: center;

      i {
        color: $landing-primary-color;
        font-size: 0.85rem;
      }
    }
  }

  .btn-pay-plan {
    background: linear-gradient(135deg, $landing-primary-color 0%, $landing-secondary-color 100%) !important;
    border: none !important;
    font-weight: 600;
    padding: 0.85rem 1.25rem;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px $landing-primary-rgba-30;
    }
  }

  .btn-cancel-plan {
    font-weight: 600;
    padding: 0.8rem 1.1rem;
    border-radius: 10px;
    border-width: 1px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    transition: all 0.2s ease;

    i {
      font-size: 0.95rem;
    }

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(220, 53, 69, 0.25);
      background-color: rgba(220, 53, 69, 0.04);
    }
  }
}

.plan-block-benefits-col {
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;

  .benefits-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: $landing-text-dark;
    margin: 0 0 1rem;
    display: flex;
    align-items: center;

    i {
      color: $landing-secondary-color;
    }
  }

  .benefits-list {
    list-style: none;
    padding: 0;
    margin: 0;
    flex: 1;
  }

  .benefit-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.6rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .benefit-check-img {
    width: 1.4rem;
    height: 1.4rem;
    flex-shrink: 0;
    margin-top: 0.15rem;
  }

  .benefit-item {
    flex: 1;
    padding: 0.65rem 1rem;
    background: $landing-bg-light;
    border: 1px solid $landing-border-light;
    border-radius: 8px;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: $landing-primary-color;
    }
  }

  .benefit-name {
    font-weight: 600;
    color: $landing-primary-color;
    font-size: 1rem;
    display: block;
  }

  .benefit-row--cancelled .benefit-item {
    background: rgba(253, 126, 20, 0.06);
    border-color: rgba(253, 126, 20, 0.2);
  }

  .benefit-row--cancelled .benefit-name {
    color: #9a3412;
  }

  .benefit-detail {
    font-size: 0.9rem;
    color: $landing-text-medium;
    display: block;
    margin-top: 0.15rem;
  }

  .no-benefits {
    padding: 1rem 0;
  }

  .plan-seller-info {
    padding: 1rem;
    background: $landing-bg-light;
    border-radius: 10px;
    border: 1px solid $landing-border-light;

    .seller-label {
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $landing-text-medium;
      margin-bottom: 0.25rem;

      i {
        color: $landing-primary-color;
      }
    }

    .seller-name {
      font-weight: 600;
      color: $landing-text-dark;
      font-size: 1rem;
    }
  }
}

/* Sección inferior: consultas y uso de beneficios */
.plan-detail-footer {
  padding: 1.5rem 1.5rem 2rem;
  border-top: 1px solid $landing-border-light;
  background: linear-gradient(180deg, $landing-bg-light 0%, $landing-bg-white 100%);
}

.detail-section {
  .detail-section-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: $landing-primary-color;
    margin: 0 0 1rem;
    display: flex;
    align-items: center;

    i {
      color: $landing-secondary-color;
    }
  }
}

.consultations-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.consultation-card {
  background: $landing-bg-white;
  border: 1px solid $landing-border-light;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: $landing-primary-rgba-25;
    box-shadow: 0 2px 10px $landing-primary-rgba-08;
  }
}

.consultation-main {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.consultation-date {
  font-size: 0.9rem;
  font-weight: 600;
  color: $landing-primary-color;

  i {
    color: $landing-secondary-color;
  }
}

.consultation-reason {
  font-size: 1rem;
  color: $landing-text-dark;
  font-weight: 500;
}

.consultation-facility {
  font-size: 0.85rem;
  color: $landing-text-medium !important;

  i {
    color: $landing-primary-color;
    opacity: 0.8;
  }
}

.benefits-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.benefit-detail-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: $landing-bg-white;
  border: 1px solid $landing-border-light;
  border-radius: 10px;
  border-left: 4px solid $landing-secondary-color;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 12px $landing-primary-rgba-08;
  }

  &.benefit-detail-card--inactive {
    border-left-color: $landing-text-light;
    opacity: 0.85;

    .benefit-detail-name {
      color: $landing-text-medium;
    }

    .benefit-detail-count {
      color: $landing-text-light;
    }

    .benefit-detail-available-date {
      color: $landing-text-medium;
      font-size: 0.8rem;
    }
  }
}

.benefit-detail-icon {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 10px;
  background: linear-gradient(135deg, $landing-primary-rgba-12 0%, $landing-secondary-rgba-10 100%);
  color: $landing-primary-color;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;

  &.benefit-detail-icon--inactive {
    background: $landing-bg-gray;
    color: $landing-text-light;
  }
}

.benefit-detail-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.benefit-detail-name {
  font-weight: 600;
  font-size: 1rem;
  color: $landing-text-dark;
}

.benefit-detail-count {
  font-size: 0.9rem;
  color: $landing-text-medium;
}

.benefit-detail-available-date {
  font-size: 0.85rem;
  color: $landing-secondary-color;
  margin-top: 0.25rem;

  i {
    opacity: 0.9;
  }
}

@media (max-width: 991px) {
  .plan-block-content {
    flex-direction: column !important;
  }

  .plan-block-image-col {
    flex: 1 1 100%;
    min-width: 100%;
  }

  .plan-block-benefits-col {
    min-width: 100%;
  }
}

@media (max-width: 576px) {
  .member-dashboard {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .plan-block-content {
    padding: 1.25rem 1rem;
  }

  .plan-block-header-inner {
    padding: 1rem 1.25rem;
  }

  .plan-block-image-col .plan-image-wrapper .plan-price-overlay .plan-price-value {
    font-size: 1.75rem;
  }

  .plan-detail-footer {
    padding: 1.25rem 1rem 1.5rem;
  }

  .benefits-detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
